import type * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * AttentionBadge is the red count of "something waits on you" (spec
 * 2026-10-27-23): new or overdue tickets, unread threads, documents to
 * validate, contracts in their notice window. Red means a person is expected
 * to act, never a plain total: a neutral count stays a `Badge` or the
 * sidebar's `SidebarMenuBadge`.
 *
 * Solid destructive token with its foreground (white in both themes), a pill
 * at least as wide as it is high, « 99+ » above 99. The caller names it with
 * `aria-label` (the count alone means nothing to a screen reader).
 */
export function formatAttentionCount(count: number): string {
    return count > 99 ? '99+' : String(count)
}

export function AttentionBadge({
    count,
    className,
    ...props
}: { count: number } & React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="attention-badge"
            className={cn(
                'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-xs font-semibold tabular-nums text-destructive-foreground select-none',
                className,
            )}
            {...props}
        >
            {formatAttentionCount(count)}
        </span>
    )
}

/**
 * AttentionDot is the badge for an icon-only sidebar: an 8 px solid dot,
 * hidden from assistive technology. The count and its meaning go to the
 * button's own `aria-label`.
 */
export function AttentionDot({ className, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            data-slot="attention-dot"
            aria-hidden="true"
            className={cn('pointer-events-none block size-2 rounded-full bg-destructive ring-2 ring-sidebar', className)}
            {...props}
        />
    )
}
